// hooks/usePublishedHeight.js
// Publish an element's rendered height as a CSS custom property on <html>, so
// anything that must sit under it (a sticky toolbar beneath the pinned
// impersonation banner, say) can lay out against it without knowing it.
//
// A ResizeObserver keeps the value live through wrapping and font loading;
// the window resize listener is the fallback for browsers without one. On
// unmount the property is removed, so a page whose bar is gone falls back to
// the `var(--x, fallback)` it declared. Same contract as the customer web
// client's hook of the same name.
import { useEffect } from "react";

const usePublishedHeight = (ref, cssVar) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !cssVar) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(cssVar, `${el.offsetHeight}px`);
    publish();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    window.addEventListener("resize", publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", publish);
      root.style.removeProperty(cssVar);
    };
  }, [ref, cssVar]);
};

export default usePublishedHeight;
